import { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, LabelList } from 'recharts';
import SalesLayout from '../../components/SalesLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

function resolveFinancialYearInfo() {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth(); // 0 = Jan, 3 = Apr

    const startYear = monthIndex >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    const shortLabel = `FY ${startYear}-${String(endYear).slice(-2)}`;
    const periodLabel = `Apr ${startYear} to Mar ${endYear}`;

    return { startYear, endYear, shortLabel, periodLabel };
}

function buildFinancialYearMonths(fy) {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Apr (4) to Dec (12) of startYear
    for (let m = 4; m <= 12; m += 1) {
        const monthKey = `${fy.startYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.startYear}`;
        months.push({ monthKey, label });
    }

    // Jan (1) to Mar (3) of endYear
    for (let m = 1; m <= 3; m += 1) {
        const monthKey = `${fy.endYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.endYear}`;
        months.push({ monthKey, label });
    }

    return months;
}

function formatAmount(value) {
    const amount = Number.isFinite(value) ? (value ?? 0) : 0;
    return `Rs ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(amount))}`;
}

function formatPercent(value) {
    const percent = Number.isFinite(value) ? (value ?? 0) : 0;
    const nonNegative = Math.max(0, percent);
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: nonNegative >= 10 ? 0 : 1 }).format(nonNegative);
    return `${formatted}%`;
}

function resolveAchievementTone(rate) {
    if (rate >= 110) {
        return 'text-emerald-600';
    }

    if (rate >= 90) {
        return 'text-emerald-500';
    }

    if (rate >= 70) {
        return 'text-amber-500';
    }

    return 'text-rose-600';
}

export default function Dashboard({ pageTitle, section }) {
    const [statusSummary, setStatusSummary] = useState([]);
    const [registrationData, setRegistrationData] = useState([]);
    const [followupData, setFollowupData] = useState([]);
    const [inquiryData, setInquiryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get('/admin/sales-dashboard');
                if (!response?.data || cancelled) return;

                const data = response.data;
                setStatusSummary(data.inquiryStatusSummary ?? []);
                setRegistrationData(data.monthlyRegistrations ?? []);
                setFollowupData(data.dailyFollowups ?? []);
                setInquiryData(data.dailyInquiries ?? []);
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load sales dashboard data.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, []);

    const totalInquiries = statusSummary.reduce(
        (sum, item) => sum + (Number.isFinite(item.count) ? item.count : 0),
        0,
    );

    const financialYearInfo = useMemo(() => resolveFinancialYearInfo(), []);
    const totalFollowups = followupData.reduce((sum, item) => sum + item.count, 0);
    const totalNewInquiries = inquiryData.reduce((sum, item) => sum + item.count, 0);
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const chartData = useMemo(() => {
        if (!financialYearInfo) {
            return registrationData.map((row) => ({
                label: row.label,
                targetAmount: row.targetAmount ?? 0,
                achievedAmount: row.registeredAmount ?? 0,
                balanceAmount: Math.max(0, (row.targetAmount ?? 0) - (row.registeredAmount ?? 0)),
                achievementRate: (row.targetAmount ?? 0) > 0 ? ((row.registeredAmount ?? 0) / (row.targetAmount ?? 0)) * 100 : 0,
            }));
        }

        const fyMonths = buildFinancialYearMonths(financialYearInfo);
        const byKey = {};
        for (const row of registrationData) {
            byKey[row.monthKey] = row;
        }

        return fyMonths.map(({ monthKey, label }) => {
            const row = byKey[monthKey];
            const target = row?.targetAmount ?? 0;
            const achieved = row?.registeredAmount ?? 0;
            const balance = Math.max(0, target - achieved);
            const rate = target > 0 ? (achieved / target) * 100 : 0;

            return {
                label,
                targetAmount: target,
                achievedAmount: achieved,
                balanceAmount: balance,
                achievementRate: rate,
            };
        });
    }, [registrationData, financialYearInfo]);

    const totals = chartData.reduce(
        (acc, row) => {
            acc.target += row.targetAmount ?? 0;
            acc.achieved += row.achievedAmount ?? 0;
            return acc;
        },
        { target: 0, achieved: 0 },
    );
    const pendingTotal = Math.max(0, totals.target - totals.achieved);
    const overallRate = totals.target > 0 ? (totals.achieved / totals.target) * 100 : 0;
    const overallRateRounded = Math.round(overallRate);

    const resolvedTitle = pageTitle || 'Sales dashboard';

    return (
        <SalesLayout title={resolvedTitle} currentSection={section}>
            {loading ? (
                <PageLoader label="Loading sales dashboard…" />
            ) : (
            <div className="space-y-4">
                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-base font-semibold text-slate-900">Inquiry status summary</h1>
                            <p className="text-sm text-slate-500">
                                Track how many student inquiries sit in each workflow state.
                            </p>
                        </div>
                        <div className="text-sm text-slate-500">
                            <span>Total inquiries</span>
                            <p className="text-xl font-semibold text-slate-900">{totalInquiries}</p>
                        </div>
                    </div>

                    <div className="mt-4 flex gap-3">
                        {statusSummary.length ? (
                            statusSummary.map((item) => (
                                <div
                                    key={item.status}
                                    className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
                                >
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        {item.status}
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-slate-900">{item.count}</p>
                                </div>
                            ))
                        ) : (
                            <div className="w-full rounded-lg border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                                Provide inquiry data to see the distribution by status.
                            </div>
                        )}
                    </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-base font-semibold text-slate-900">Daily follow-up activity</h1>
                            <p className="text-sm text-slate-500">
                                Track your daily follow-up momentum throughout {currentMonth}.
                            </p>
                        </div>
                        <div className="text-sm text-slate-500">
                            <span>Total follow-ups</span>
                            <p className="text-xl font-semibold text-slate-900">{totalFollowups}</p>
                        </div>
                    </div>

                    {followupData.length ? (
                        <div className="mt-6">
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={followupData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 11 }}
                                        />
                                        <YAxis
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 11 }}
                                            allowDecimals={false}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.875rem',
                                            }}
                                            formatter={(value) => [value, 'Follow-ups']}
                                        />
                                        <Bar dataKey="count" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
                            No follow-up activity recorded this month yet.
                        </div>
                    )}
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-base font-semibold text-slate-900">New inquiries added daily</h1>
                            <p className="text-sm text-slate-500">
                                Track new student inquiries captured each day in {currentMonth}.
                            </p>
                        </div>
                        <div className="text-sm text-slate-500">
                            <span>Total new inquiries</span>
                            <p className="text-xl font-semibold text-slate-900">{totalNewInquiries}</p>
                        </div>
                    </div>

                    {inquiryData.length ? (
                        <div className="mt-6">
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={inquiryData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 11 }}
                                        />
                                        <YAxis
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 11 }}
                                            allowDecimals={false}
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.875rem',
                                            }}
                                            formatter={(value) => [value, 'New inquiries']}
                                        />
                                        <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
                            No new inquiries recorded this month yet.
                        </div>
                    )}
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-base font-semibold text-slate-900">Month-wise registrations</h1>
                            <p className="text-sm text-slate-500">
                                Track how actual registration revenue stacks up against the monthly targets set for the team.
                            </p>
                        </div>
                        {financialYearInfo ? (
                            <p className="text-xs uppercase tracking-wide text-slate-500">
                                {financialYearInfo.periodLabel}
                            </p>
                        ) : null}
                    </div>

                    {chartData.length ? (
                        <div className="mt-6 space-y-3">
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 12 }}
                                        />
                                        <YAxis
                                            stroke="#64748b"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) =>
                                                new Intl.NumberFormat('en-US', {
                                                    notation: 'compact',
                                                    compactDisplay: 'short',
                                                }).format(value)
                                            }
                                        />
                                        <Tooltip
                                            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.875rem',
                                            }}
                                            formatter={(value, name) => {
                                                if (name === 'targetAmount') return [formatAmount(value), 'Target amount'];
                                                if (name === 'achievedAmount') return [formatAmount(value), 'Achieved amount'];
                                                return [value, name];
                                            }}
                                        />
                                        <Bar dataKey="targetAmount" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="achievedAmount" fill="#10b981" radius={[6, 6, 0, 0]}>
                                            <LabelList
                                                dataKey="achievementRate"
                                                position="top"
                                                formatter={(value) => (value > 0 ? `${Math.round(value)}%` : '')}
                                                style={{ fontSize: '0.75rem', fontWeight: 600 }}
                                                fill="#0f172a"
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-3">
                                <div className="rounded-xl bg-slate-100 px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                        Total target
                                    </p>
                                    <p className="mt-2 text-xl font-semibold text-slate-900">
                                        {formatAmount(totals.target)}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-emerald-100 px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                                        Total achieved
                                    </p>
                                    <p className="mt-2 text-xl font-semibold text-emerald-900">
                                        {formatAmount(totals.achieved)}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-amber-100 px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                                        Pending amount
                                    </p>
                                    <p className="mt-2 text-xl font-semibold text-amber-900">
                                        {formatAmount(pendingTotal)}
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-700">Overall achievement</p>
                                    <p className={`text-2xl font-bold ${resolveAchievementTone(overallRateRounded)}`}>
                                        {overallRateRounded}%
                                    </p>
                                </div>
                                <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-200">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-300"
                                        style={{ width: `${Math.min(100, overallRateRounded)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
                            No monthly registration data available yet.
                        </div>
                    )}
                </section>
            </div>
            )}
        </SalesLayout>
    );
}
